import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge } from "../components/common";
import { QUOTE_STATUSES, JOB_STATUSES, itDate } from "../lib/constants";
import TaskDialog from "../components/TaskDialog";
import { TaskList } from "../components/TaskList";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ExternalLink, CheckCircle2, Trash2, Plus } from "lucide-react";

// kind -> sources and the "complete" action for each
const KINDS = {
  sopralluoghi: { title: "Sopralluoghi da programmare", quotes: ["bozza"], jobs: ["sopralluogo"], quoteNext: ["sopralluogo_programmato", "Segna programmato"], jobNext: ["preventivo_da_fare", "Sopralluogo fatto"] },
  preventivi: { title: "Preventivi da preparare", quotes: ["bozza", "sopralluogo_eseguito"], jobs: [], quoteNext: ["inviato", "Segna inviato"], task: "preventivo" },
  materiali: { title: "Materiali da ordinare", quotes: [], jobs: ["confermato"], jobNext: ["materiale_ordinato", "Segna ordinato"], task: "materiale" },
  montaggi: { title: "Montaggi da programmare", quotes: [], jobs: ["materiale_ordinato", "materiale_pronto"], jobNext: ["montaggio_programmato", "Segna programmato"] },
};

function Row({ item, isQuote, next, onOpen, onNext, onDelete, canEdit }) {
  const [ask, setAsk] = useState(false);
  return (
    <div data-testid={`dg-row-${item.id}`} className="card-soft p-3 flex flex-col sm:flex-row sm:items-center gap-2">
      <div className="min-w-0 flex-1 cursor-pointer" onClick={onOpen}>
        <p className="font-medium text-sm truncate">{item.client_name || "—"} <span className="text-muted-foreground">· {item.product || item.code || (isQuote ? "Preventivo" : "Lavoro")}</span></p>
        <div className="flex items-center gap-2 mt-1"><StatusBadge list={isQuote ? QUOTE_STATUSES : JOB_STATUSES} value={item.status} /><span className="text-xs text-muted-foreground">{isQuote ? `Preventivo ${item.code || ""}` : "Lavoro"} · {itDate(item.created_at)}</span></div>
      </div>
      <div className="flex gap-1.5 shrink-0">
        <Button size="sm" variant="outline" data-testid={`dg-open-${item.id}`} onClick={onOpen}><ExternalLink size={14} className="mr-1" /> Apri</Button>
        {canEdit && next && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`dg-next-${item.id}`} onClick={onNext}><CheckCircle2 size={14} className="mr-1" /> {next[1]}</Button>}
        {canEdit && (ask ? <Button size="sm" variant="destructive" data-testid={`dg-delete-confirm-${item.id}`} onClick={onDelete}>Conferma</Button>
          : <Button size="sm" variant="outline" className="text-destructive border-destructive/40" data-testid={`dg-delete-${item.id}`} onClick={() => setAsk(true)}><Trash2 size={14} /></Button>)}
      </div>
    </div>
  );
}

export default function DaGestire() {
  const { kind } = useParams();
  const cfg = KINDS[kind] || KINDS.sopralluoghi;
  const { can } = useAuth();
  const nav = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [dlg, setDlg] = useState({ open: false, task: null });

  const load = async () => {
    const qs = cfg.quotes.length && can("view_quotes") ? (await Promise.all(cfg.quotes.map((s) => api.get(`/quotes?status=${s}`)))).flatMap((r) => r.data) : [];
    const js = cfg.jobs.length ? (await Promise.all(cfg.jobs.map((s) => api.get(`/jobs?status=${s}`)))).flatMap((r) => r.data) : [];
    setQuotes(qs); setJobs(js);
    if (cfg.task) { const r = await api.get(`/tasks?kind=${cfg.task}&state=open`); setTasks(r.data); } else setTasks([]);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);
  useEffect(() => { if (cfg.task) api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, [cfg.task]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); } };
  const total = quotes.length + jobs.length + tasks.length;

  return (
    <div>
      <PageHeader title={cfg.title} subtitle={`${total} ${total === 1 ? "voce" : "voci"} da gestire`}>
        <Button variant="outline" data-testid="dg-back" onClick={() => nav("/")}>← Dashboard</Button>
        {cfg.task && <Button className="bg-brand hover:bg-[#A8472F] text-white" data-testid="dg-add-task" onClick={() => setDlg({ open: true, task: null })}><Plus size={16} className="mr-1" /> Aggiungi</Button>}
      </PageHeader>
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.entries(KINDS).map(([k, c]) => <button key={k} data-testid={`dg-tab-${k}`} onClick={() => nav(`/da-gestire/${k}`)} className={`text-xs px-3 py-1.5 rounded-full border ${k === kind ? "bg-ink text-white" : "bg-white text-muted-foreground"}`}>{c.title}</button>)}
      </div>
      {total === 0 && <div className="card-soft p-10 text-center text-muted-foreground" data-testid="dg-empty">Tutto in ordine: niente da gestire qui.</div>}
      {cfg.task && tasks.length > 0 && (
        <div className="mb-5"><p className="text-xs font-semibold text-muted-foreground mb-2">Promemoria aggiunti dalla dashboard</p>
          <TaskList kind={cfg.task} items={tasks} onOpen={(t) => setDlg({ open: true, task: t })} onChanged={load} empty="" />
        </div>
      )}
      {(quotes.length > 0 || jobs.length > 0) && (
        <div className="space-y-2">
          {quotes.map((q) => <Row key={q.id} item={q} isQuote next={cfg.quoteNext} canEdit={can("edit_quotes")} onOpen={() => nav(`/preventivi?edit=${q.id}`)}
            onNext={() => act(() => api.post(`/quotes/${q.id}/status`, { status: cfg.quoteNext[0], note: "Da dashboard" }), "Stato aggiornato")}
            onDelete={() => act(() => api.delete(`/quotes/${q.id}`), "Preventivo eliminato")} />)}
          {jobs.map((j) => <Row key={j.id} item={j} next={cfg.jobNext} canEdit={can("edit_jobs")} onOpen={() => nav(`/lavori/${j.id}`)}
            onNext={() => act(() => api.put(`/jobs/${j.id}`, { status: cfg.jobNext[0] }), "Stato aggiornato")}
            onDelete={() => act(() => api.delete(`/jobs/${j.id}`), "Lavoro eliminato")} />)}
        </div>
      )}
      {cfg.task && <TaskDialog open={dlg.open} kind={cfg.task} task={dlg.task} clients={clients} onClose={() => setDlg({ open: false, task: null })} onChanged={load} />}
    </div>
  );
}
